"use client";

import type { ApolloClient } from "@apollo/client/core";
import { useApolloClient } from "@apollo/client/react";
import { batch, signal } from "@preact/signals-react";
import { useContext, useEffect } from "react";

import {
  SubscriptionTiersByCreatorPublicKeyDocument,
  type SubscriptionTiersByCreatorPublicKeyQuery,
  type SubscriptionTiersByCreatorPublicKeyQueryVariables,
} from "@/graphql/codegen/graphql";
import { LocalStorageKeys } from "@/utils/local-storage-keys";
import {
  getSingleDerivedKey,
  parseTier,
  type TierSchema,
} from "@/utils/subscriptions";

import { UserContext } from "./user-context";

export type SubscriptionTierHealthIssue = {
  accessGroupKeyName: string;
  tierName: string;
  derivedPublicKey: string;
  expirationBlock: number | null;
  type: "creator-access-key-inactive";
};

type SubscriptionTierHealthState = {
  accountPublicKey: string | null;
  status: "idle" | "checking" | "ready";
  checkedAt: string | null;
  issues: SubscriptionTierHealthIssue[];
};

type SubscriptionTierHealthCacheRecord = {
  version: number;
  checkedAt: string | null;
  issues: SubscriptionTierHealthIssue[];
};

type SubscriptionTierHealthCheckPriority = "idle" | "visible";

const SUBSCRIPTION_TIER_HEALTH_CACHE_VERSION = 1;
const initialSubscriptionTierHealthState: SubscriptionTierHealthState = {
  accountPublicKey: null,
  status: "idle",
  checkedAt: null,
  issues: [],
};

type ScheduledCheckRecord = {
  handle: number;
  kind: "idle" | "timeout";
  priority: SubscriptionTierHealthCheckPriority;
};

const scheduledChecksByAccount = new Map<string, ScheduledCheckRecord>();
const inFlightChecksByAccount = new Map<
  string,
  Promise<SubscriptionTierHealthIssue[]>
>();

export const subscriptionTierHealthSignal = signal<SubscriptionTierHealthState>(
  initialSubscriptionTierHealthState,
);

const getSubscriptionTierHealthStorageKey = (accountPublicKey: string) =>
  `${LocalStorageKeys.subscriptionTierHealth}:${accountPublicKey}`;

const readSubscriptionTierHealthCache = (
  accountPublicKey: string,
): SubscriptionTierHealthCacheRecord | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const rawValue = window.localStorage.getItem(
    getSubscriptionTierHealthStorageKey(accountPublicKey),
  );

  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as SubscriptionTierHealthCacheRecord;

    if (
      parsed?.version !== SUBSCRIPTION_TIER_HEALTH_CACHE_VERSION ||
      !Array.isArray(parsed.issues)
    ) {
      return null;
    }

    return {
      version: parsed.version,
      checkedAt: parsed.checkedAt ?? null,
      issues: parsed.issues,
    };
  } catch {
    return null;
  }
};

const writeSubscriptionTierHealthCache = ({
  accountPublicKey,
  issues,
  checkedAt = new Date().toISOString(),
}: {
  accountPublicKey: string;
  issues: SubscriptionTierHealthIssue[];
  checkedAt?: string | null;
}) => {
  if (typeof window === "undefined") {
    return;
  }

  const record: SubscriptionTierHealthCacheRecord = {
    version: SUBSCRIPTION_TIER_HEALTH_CACHE_VERSION,
    checkedAt,
    issues,
  };

  window.localStorage.setItem(
    getSubscriptionTierHealthStorageKey(accountPublicKey),
    JSON.stringify(record),
  );
};

const setSubscriptionTierHealthState = ({
  accountPublicKey,
  issues,
  checkedAt,
  status,
}: {
  accountPublicKey: string;
  issues: SubscriptionTierHealthIssue[];
  checkedAt: string | null;
  status: SubscriptionTierHealthState["status"];
}) => {
  subscriptionTierHealthSignal.value = {
    accountPublicKey,
    issues,
    checkedAt,
    status,
  };
};

const primeSubscriptionTierHealthState = (accountPublicKey: string) => {
  if (
    subscriptionTierHealthSignal.value.accountPublicKey === accountPublicKey
  ) {
    return;
  }

  const cachedRecord = readSubscriptionTierHealthCache(accountPublicKey);

  if (cachedRecord) {
    setSubscriptionTierHealthState({
      accountPublicKey,
      issues: cachedRecord.issues,
      checkedAt: cachedRecord.checkedAt,
      status: "ready",
    });
    return;
  }

  setSubscriptionTierHealthState({
    accountPublicKey,
    issues: [],
    checkedAt: null,
    status: "idle",
  });
};

const cancelScheduledSubscriptionTierHealthCheckForAccount = (
  accountPublicKey: string,
) => {
  const scheduledRecord = scheduledChecksByAccount.get(accountPublicKey);

  if (!scheduledRecord || typeof window === "undefined") {
    return;
  }

  if (
    scheduledRecord.kind === "idle" &&
    typeof window.cancelIdleCallback === "function"
  ) {
    window.cancelIdleCallback(scheduledRecord.handle);
  } else {
    window.clearTimeout(scheduledRecord.handle);
  }

  scheduledChecksByAccount.delete(accountPublicKey);
};

const resolveSubscriptionTierHealthIssues = async ({
  client,
  accountPublicKey,
}: {
  client: ApolloClient;
  accountPublicKey: string;
}) => {
  const { data } = await client.query<
    SubscriptionTiersByCreatorPublicKeyQuery,
    SubscriptionTiersByCreatorPublicKeyQueryVariables
  >({
    query: SubscriptionTiersByCreatorPublicKeyDocument,
    variables: {
      filter: {
        accessGroupOwnerPublicKey: {
          equalTo: accountPublicKey,
        },
        paymentCadenceDays: {
          notEqualTo: -1,
        },
      },
      viewerPublicKey: accountPublicKey,
    },
    fetchPolicy: "network-only",
  });

  const tiers: TierSchema[] = (data?.subscriptionTiers?.nodes ?? [])
    .filter((tier): tier is NonNullable<typeof tier> => !!tier)
    .map(parseTier);

  const issueResults = await Promise.all(
    tiers.map(async (tier): Promise<SubscriptionTierHealthIssue | null> => {
      return resolveSubscriptionTierHealthIssue({
        accountPublicKey,
        tier,
      });
    }),
  );

  return issueResults.filter(
    (issue): issue is SubscriptionTierHealthIssue => !!issue,
  );
};

const resolveSubscriptionTierHealthIssue = async ({
  accountPublicKey,
  tier,
}: {
  accountPublicKey: string;
  tier: TierSchema;
}) => {
  const derivedPublicKey = tier.accessGroupDerivedPublicKey;

  if (!derivedPublicKey) {
    return {
      accessGroupKeyName: tier.accessGroupKeyName,
      tierName: tier.name,
      derivedPublicKey: "",
      expirationBlock: null,
      type: "creator-access-key-inactive" as const,
    };
  }

  const derivedKey = await getSingleDerivedKey({
    ownerPublicKey: accountPublicKey,
    derivedPublicKey,
  });

  if (derivedKey?.IsValid) {
    return null;
  }

  return {
    accessGroupKeyName: tier.accessGroupKeyName,
    tierName: tier.name,
    derivedPublicKey,
    expirationBlock: derivedKey?.ExpirationBlock ?? null,
    type: "creator-access-key-inactive" as const,
  };
};

const runSubscriptionTierHealthCheck = async ({
  client,
  accountPublicKey,
  force = false,
}: {
  client: ApolloClient;
  accountPublicKey: string;
  force?: boolean;
}) => {
  primeSubscriptionTierHealthState(accountPublicKey);

  if (!force) {
    const cachedRecord = readSubscriptionTierHealthCache(accountPublicKey);

    if (cachedRecord) {
      setSubscriptionTierHealthState({
        accountPublicKey,
        issues: cachedRecord.issues,
        checkedAt: cachedRecord.checkedAt,
        status: "ready",
      });
      return cachedRecord.issues;
    }
  }

  const existingCheck = inFlightChecksByAccount.get(accountPublicKey);

  if (existingCheck) {
    return existingCheck;
  }

  setSubscriptionTierHealthState({
    accountPublicKey,
    issues:
      subscriptionTierHealthSignal.value.accountPublicKey === accountPublicKey
        ? subscriptionTierHealthSignal.value.issues
        : [],
    checkedAt:
      subscriptionTierHealthSignal.value.accountPublicKey === accountPublicKey
        ? subscriptionTierHealthSignal.value.checkedAt
        : null,
    status: "checking",
  });

  const checkPromise = (async () => {
    try {
      const issues = await resolveSubscriptionTierHealthIssues({
        client,
        accountPublicKey,
      });
      const checkedAt = new Date().toISOString();

      writeSubscriptionTierHealthCache({
        accountPublicKey,
        issues,
        checkedAt,
      });

      if (
        subscriptionTierHealthSignal.value.accountPublicKey === accountPublicKey
      ) {
        setSubscriptionTierHealthState({
          accountPublicKey,
          issues,
          checkedAt,
          status: "ready",
        });
      }

      return issues;
    } finally {
      inFlightChecksByAccount.delete(accountPublicKey);
    }
  })();

  inFlightChecksByAccount.set(accountPublicKey, checkPromise);

  return checkPromise;
};

export const queueSubscriptionTierHealthCheck = ({
  client,
  accountPublicKey,
  force = false,
  priority = "idle",
}: {
  client: ApolloClient;
  accountPublicKey: string;
  force?: boolean;
  priority?: SubscriptionTierHealthCheckPriority;
}) => {
  if (typeof window === "undefined" || !accountPublicKey) {
    return;
  }

  primeSubscriptionTierHealthState(accountPublicKey);

  if (!force) {
    const cachedRecord = readSubscriptionTierHealthCache(accountPublicKey);

    if (cachedRecord) {
      setSubscriptionTierHealthState({
        accountPublicKey,
        issues: cachedRecord.issues,
        checkedAt: cachedRecord.checkedAt,
        status: "ready",
      });
      return;
    }
  }

  const scheduledRecord = scheduledChecksByAccount.get(accountPublicKey);

  if (scheduledRecord) {
    if (
      scheduledRecord.priority === priority ||
      scheduledRecord.priority === "visible"
    ) {
      return;
    }

    cancelScheduledSubscriptionTierHealthCheckForAccount(accountPublicKey);
  }

  const run = () => {
    scheduledChecksByAccount.delete(accountPublicKey);
    void runSubscriptionTierHealthCheck({
      client,
      accountPublicKey,
      force,
    });
  };

  if (priority === "visible") {
    const timeoutHandle = window.setTimeout(run, 0);
    scheduledChecksByAccount.set(accountPublicKey, {
      handle: timeoutHandle,
      kind: "timeout",
      priority,
    });
    return;
  }

  if (typeof window.requestIdleCallback === "function") {
    const idleHandle = window.requestIdleCallback(run, {
      timeout: 1200,
    });
    scheduledChecksByAccount.set(accountPublicKey, {
      handle: idleHandle,
      kind: "idle",
      priority,
    });
    return;
  }

  const timeoutHandle = window.setTimeout(run, 200);
  scheduledChecksByAccount.set(accountPublicKey, {
    handle: timeoutHandle,
    kind: "timeout",
    priority,
  });
};

export const hydrateSubscriptionTierHealth = (accountPublicKey: string) => {
  primeSubscriptionTierHealthState(accountPublicKey);
  return readSubscriptionTierHealthCache(accountPublicKey);
};

export const clearSubscriptionTierHealth = () => {
  batch(() => {
    for (const accountPublicKey of scheduledChecksByAccount.keys()) {
      cancelScheduledSubscriptionTierHealthCheckForAccount(accountPublicKey);
    }
    subscriptionTierHealthSignal.value = initialSubscriptionTierHealthState;
  });
};

export const invalidateSubscriptionTierHealth = (accountPublicKey: string) => {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(
      getSubscriptionTierHealthStorageKey(accountPublicKey),
    );
  }

  cancelScheduledSubscriptionTierHealthCheckForAccount(accountPublicKey);

  if (
    subscriptionTierHealthSignal.value.accountPublicKey !== accountPublicKey
  ) {
    return;
  }

  setSubscriptionTierHealthState({
    accountPublicKey,
    issues: [],
    checkedAt: null,
    status: "idle",
  });
};

export const resolveSubscriptionTierHealthIssueForTier = ({
  accountPublicKey,
  accessGroupKeyName,
}: {
  accountPublicKey: string;
  accessGroupKeyName: string;
}) => {
  const currentSignalValue = subscriptionTierHealthSignal.value;
  const currentIssues =
    currentSignalValue.accountPublicKey === accountPublicKey
      ? currentSignalValue.issues
      : (readSubscriptionTierHealthCache(accountPublicKey)?.issues ?? []);
  const nextIssues = currentIssues.filter(
    (issue) => issue.accessGroupKeyName !== accessGroupKeyName,
  );
  const checkedAt =
    currentSignalValue.accountPublicKey === accountPublicKey
      ? currentSignalValue.checkedAt
      : (readSubscriptionTierHealthCache(accountPublicKey)?.checkedAt ?? null);

  writeSubscriptionTierHealthCache({
    accountPublicKey,
    issues: nextIssues,
    checkedAt,
  });

  if (currentSignalValue.accountPublicKey !== accountPublicKey) {
    return;
  }

  setSubscriptionTierHealthState({
    accountPublicKey,
    issues: nextIssues,
    checkedAt,
    status: "ready",
  });
};

export const SubscriptionTierHealthBootstrap = () => {
  const client = useApolloClient();
  const { currentUserPublicKey, loggedIn } = useContext(UserContext);

  useEffect(() => {
    if (!loggedIn || !currentUserPublicKey) {
      clearSubscriptionTierHealth();
      return;
    }

    const cachedRecord = hydrateSubscriptionTierHealth(currentUserPublicKey);

    if (!cachedRecord) {
      queueSubscriptionTierHealthCheck({
        client,
        accountPublicKey: currentUserPublicKey,
        priority: "idle",
      });
    }

    return () => {
      cancelScheduledSubscriptionTierHealthCheckForAccount(
        currentUserPublicKey,
      );
    };
  }, [client, currentUserPublicKey, loggedIn]);

  return null;
};
